import { useState, useEffect, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import type {
  PhaseWithSubGroups,
  TemplateType,
  TaskTemplateWithDetails,
  JobTitle,
} from '../../../../types';
import { taskTemplatesApi } from '../../../../services/api/taskTemplates';
import { jobTitlesApi } from '../../../../services/api/jobTitles';

interface UseTaskTemplatesOptions {
  templateType: TemplateType;
  includeArchived?: boolean;
}

interface UseTaskTemplatesResult {
  // Data
  phases: PhaseWithSubGroups[];
  jobTitles: JobTitle[];
  loading: boolean;
  error: string | null;

  // Actions
  refresh: () => Promise<void>;
  archiveTemplate: (id: string) => Promise<boolean>;
  restoreTemplate: (id: string) => Promise<boolean>;
  duplicateTemplate: (id: string) => Promise<TaskTemplateWithDetails | null>;
  deleteTemplate: (id: string) => Promise<boolean>;
}

export function useTaskTemplates({
  templateType,
  includeArchived = false,
}: UseTaskTemplatesOptions): UseTaskTemplatesResult {
  const [phases, setPhases] = useState<PhaseWithSubGroups[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGroupedTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskTemplatesApi.getGrouped(templateType, includeArchived);
      setPhases(data || []);
    } catch (err) {
      console.error('Failed to fetch grouped templates:', err);
      setError('Failed to load task templates');
      notifications.show({
        title: 'Error',
        message: 'Failed to load task templates',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [templateType, includeArchived]);

  const fetchJobTitles = useCallback(async () => {
    try {
      const data = await jobTitlesApi.getAll();
      setJobTitles(data || []);
    } catch (err) {
      console.error('Failed to fetch job titles:', err);
    }
  }, []);

  useEffect(() => {
    fetchGroupedTemplates();
  }, [fetchGroupedTemplates]);

  useEffect(() => {
    fetchJobTitles();
  }, [fetchJobTitles]);

  const archiveTemplate = useCallback(async (id: string): Promise<boolean> => {
    try {
      await taskTemplatesApi.archive(id);
      notifications.show({
        title: 'Success',
        message: 'Task archived',
        color: 'green',
      });
      await fetchGroupedTemplates();
      return true;
    } catch (err) {
      console.error('Failed to archive template:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to archive template',
        color: 'red',
      });
      return false;
    }
  }, [fetchGroupedTemplates]);

  const restoreTemplate = useCallback(async (id: string): Promise<boolean> => {
    try {
      await taskTemplatesApi.restore(id);
      notifications.show({
        title: 'Success',
        message: 'Task restored',
        color: 'green',
      });
      await fetchGroupedTemplates();
      return true;
    } catch (err) {
      console.error('Failed to restore template:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to restore template',
        color: 'red',
      });
      return false;
    }
  }, [fetchGroupedTemplates]);

  const duplicateTemplate = useCallback(async (id: string): Promise<TaskTemplateWithDetails | null> => {
    try {
      const result = await taskTemplatesApi.duplicate(id);
      notifications.show({
        title: 'Success',
        message: 'Task duplicated',
        color: 'green',
      });
      await fetchGroupedTemplates();
      return result;
    } catch (err) {
      console.error('Failed to duplicate template:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to duplicate template',
        color: 'red',
      });
      return null;
    }
  }, [fetchGroupedTemplates]);

  const deleteTemplate = useCallback(async (id: string): Promise<boolean> => {
    try {
      await taskTemplatesApi.delete(id);
      notifications.show({
        title: 'Success',
        message: 'Task deleted',
        color: 'green',
      });
      await fetchGroupedTemplates();
      return true;
    } catch (err) {
      console.error('Failed to delete template:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete template',
        color: 'red',
      });
      return false;
    }
  }, [fetchGroupedTemplates]);

  return {
    phases,
    jobTitles,
    loading,
    error,
    refresh: fetchGroupedTemplates,
    archiveTemplate,
    restoreTemplate,
    duplicateTemplate,
    deleteTemplate,
  };
}

export default useTaskTemplates;
